import { Avatar, IconButton, ListItem, ListItemAvatar, ListItemText, Tooltip, Typography } from "@mui/material";
import { theme } from "src/theme/mui-theme";
import { alpha } from "@mui/material/styles";
import { ts, ts2 } from 'src/utils/translate';
import { NotificationsCheckmarkIcon } from "src/components/AppHeader/icons/NotificationsCheckmarkIcon";
import { GetProjectNotificationsResponseItem } from "src/model/api/get-project-notifications-response.model";
import { AcceptInviteRequest } from "src/model/api/accept-invite-request.model";
import { showSnackBar } from "src/api/ui-slice";
import { useAcceptInviteMutation } from "src/api/v1/project-ApiSlice";
import { useAppDispatch } from "src/app/hooks";
import { useNavigate } from "react-router";
import dayjs from "dayjs";

export function NotificationRow(props: {
    notification: GetProjectNotificationsResponseItem;
}) {
    const [acceptInvite] = useAcceptInviteMutation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { projectId, roleId, projectName, hasUserAcceptedOccupation, createdAt, userFirstName, userLastName, userAvatarLink } = props.notification;

    let userInitials: string;

    if (userFirstName && userLastName) {
        if (userFirstName && userLastName) {
            userInitials = userFirstName[0] + userLastName[0];
        } else {
            userInitials = "UP";
        }
    } else {
        userInitials = "UP";
    }

    async function onAcceptInviteClick(
        projectId: number,
        roleId: number,
        onSuccess?: () => void
    ) {
        try {
            await acceptInvite({ project_id: projectId, role_id: roleId } as AcceptInviteRequest).unwrap();

            dispatch(showSnackBar({ text: ts('role_accept_successful', 'Role accept successful'), severity: 'info' }));

            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            dispatch(showSnackBar({ text: ts('role_accept_failed', 'Failed to accept role'), severity: 'error' }));
        }
    }

    function getRelativeTime(inputDate: string | Date): string {
        return dayjs(inputDate).fromNow();
    }

    return (
        <ListItem
            alignItems="flex-start"
            sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.07)
                },
            }}
        >
            <ListItemAvatar sx={{ minWidth: "35px" }}>
                {userAvatarLink ? (
                    <Avatar
                        sx={{ width: 35, height: 35, borderRadius: 2 }}
                        src={userAvatarLink}
                    />
                ) : (
                    <Avatar sx={{ width: 35, height: 35, borderRadius: 2 }}>
                        {userInitials ?? "UP"}
                    </Avatar>
                )}
            </ListItemAvatar>

            <ListItemText
                sx={{
                    cursor: "pointer"
                }}
                onClick={
                    hasUserAcceptedOccupation === 0
                        ? () => onAcceptInviteClick(projectId, roleId, () => navigate(`/planner/${projectId}`))
                        : undefined
                }
                primary={
                    <Typography variant="body2">
                        {(() => {
                            const parts = ts2(
                                'notifications_message',
                                ' has invited to join {projectName} project',
                                { projectName: '*' }
                            ).split('*');

                            return (
                                <>
                                    {userFirstName && userLastName
                                        ? `${userFirstName} ${userLastName}`
                                        : "uPlan User"}
                                    {parts[0]}
                                    '<span style={{ fontWeight: 600 }}>{projectName}</span>'
                                    {parts[1]}
                                </>
                            );
                        })()}
                    </Typography>
                }
                secondary={
                    <Typography variant="caption" color="text.secondary">
                        {createdAt ? getRelativeTime(createdAt) : "-"}
                    </Typography>
                }
            />

            {hasUserAcceptedOccupation === 0 ? (
                <Tooltip
                    title={ts('mark_as_read', 'Mark as read')}
                    placement="top"
                    PopperProps={{
                        modifiers: [
                            {
                                name: "offset",
                                options: { offset: [-30, -10] },
                            },
                        ],
                    }}
                >
                    <IconButton
                        size="small"
                        sx={{
                            marginTop: "2px",
                            color: theme.palette.primary.main,
                            backgroundColor: 'transparent',
                        }}
                        onClick={() => onAcceptInviteClick(projectId, roleId)}
                    >
                        <NotificationsCheckmarkIcon style={{ width: 20, height: 20 }} />
                    </IconButton>
                </Tooltip>
            ) : (
                <IconButton
                    size="small"
                    sx={{
                        marginTop: "2px",
                        color: theme.palette.grey[500],
                        backgroundColor: 'transparent',
                    }}
                >
                    <NotificationsCheckmarkIcon style={{ width: 20, height: 20 }} />
                </IconButton>
            )}
        </ListItem>
    );
}
